import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import '../styles/productPage.css';
import aiWorkflowDark from '../assets/ai-investigation-workflow.svg';
import aiWorkflowLight from '../assets/ai-investigation-workflow-light.svg';

const TABS = ['overview', 'analytics', 'caseManagement', 'compliance', 'taskManagement'];

const ANALYTICS_KEYS = [
  'anomalyDetection', 'trendAnalysis', 'dataCorrelation', 'alertEngine',
  'taskForce', 'chainOfCommand', 'autoAssign', 'reportsDashboard',
  'universalSearch',
];

const AIInvestigation = () => {
  const { t } = useTranslation('aiInvestigation');
  const { language } = useLanguage();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const aiWorkflowSvg = theme === 'light' ? aiWorkflowLight : aiWorkflowDark;

  const isLight = theme === 'light';
  const commandOverviewImg = isLight ? '/Images/CommandOverview-light.png' : '/Images/CommandOverview.png';
  const investigationImg = isLight ? '/Images/InvestigationDashboard1-light.png' : '/Images/InvestigationDashboard1.png';

  return (
    <div className="product-page">
      <SEO
        title="AI Investigation – Operational Analytics & Compliance | EWANDS"
        description="Analyse large operational datasets to detect anomalies, manage investigations, and generate audit-ready intelligence for regulatory compliance."
        keywords="AI investigation, anomaly detection, case management, operational analytics, compliance reporting, EWANDS"
        canonicalPath={`/${language}/ai-investigation`}
      />

      {/* Hero */}
      <section className="product-hero">
        <div className="container">
          <div className="product-hero-inner">
            <span className="product-hero-tag">{t('overview.tag')}</span>
            <h1>{t('hero.title')}</h1>
            <p className="product-hero-subtitle">{t('hero.subtitle')}</p>
            <p className="product-hero-desc">{t('hero.description')}</p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="product-tabs-section">
        <div className="product-tab-bar-wrapper">
          <div className="container">
            <div className="product-tab-bar">
              {TABS.map((tab, i) => (
                <button
                  key={tab}
                  className={`product-tab-btn${activeTab === i ? ' active' : ''}`}
                  onClick={() => setActiveTab(i)}
                >
                  {t(`tabs.${tab}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="product-tab-content">
          <div className="container">

            {/* ── Overview ───────────────────────────────── */}
            {activeTab === 0 && (
              <div>
                <span className="pp-section-tag">{t('overview.tag')}</span>
                <h2 className="pp-section-heading">{t('overview.heading')}</h2>
                <p className="pp-section-desc">{t('overview.description')}</p>

                <div className="pp-highlights">
                  {['h1','h2','h3','h4'].map(k => (
                    <div className="pp-highlight-card" key={k}>
                      <div className="pp-highlight-label">{t(`overview.highlights.${k}.label`)}</div>
                      <div className="pp-highlight-desc">{t(`overview.highlights.${k}.desc`)}</div>
                    </div>
                  ))}
                </div>

                <div className="row g-4 mt-2">
                  <div className="col-lg-6">
                    <img
                      src={commandOverviewImg}
                      alt={t('overview.imgAlt1')}
                      className="product-overview-img"
                    />
                  </div>
                  <div className="col-lg-6">
                    <img
                      src={investigationImg}
                      alt={t('overview.imgAlt2')}
                      className="product-overview-img"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── Analytics ──────────────────────────────── */}
            {activeTab === 1 && (
              <div>
                <span className="pp-section-tag">{t('analytics.tag')}</span>
                <h2 className="pp-section-heading">{t('analytics.heading')}</h2>
                <p className="pp-section-desc">{t('analytics.subheading')}</p>

                <div className="row g-4 mb-5">
                  <div className="col-12">
                    <img
                      src={aiWorkflowSvg}
                      alt={t('analytics.imgAlt1')}
                      style={{ width: '100%', borderRadius: '12px', display: 'block' }}
                    />
                  </div>
                </div>

                <div className="pp-feature-grid">
                  {ANALYTICS_KEYS.map(key => (
                    <div
                      className={`pp-feature-card${key === 'universalSearch' ? ' pp-feature-card--wide' : ''}`}
                      key={key}
                    >
                      <span className="pp-feature-tag">{t(`analytics.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`analytics.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`analytics.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Case Management ────────────────────────── */}
            {activeTab === 2 && (
              <div>
                <span className="pp-section-tag">{t('caseManagement.tag')}</span>
                <h2 className="pp-section-heading">{t('caseManagement.heading')}</h2>

                <div className="pp-feature-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  {['caseCreation','evidenceTagging','auditTrail','escalation'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`caseManagement.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`caseManagement.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`caseManagement.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Compliance ─────────────────────────────── */}
            {activeTab === 3 && (
              <div>
                <span className="pp-section-tag">{t('compliance.tag')}</span>
                <h2 className="pp-section-heading">{t('compliance.heading')}</h2>

                <div className="pp-feature-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  {['regulatoryReporting','auditExport','chainOfCustody','retentionPolicy'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`compliance.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`compliance.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`compliance.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Task Management ────────────────────────── */}
            {activeTab === 4 && (
              <div>
                <span className="pp-section-tag">{t('taskManagement.tag')}</span>
                <h2 className="pp-section-heading">{t('taskManagement.heading')}</h2>

                <div className="pp-feature-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  {['campaignPlanning','externalCollaboration','progressTracking','operationClose'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`taskManagement.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`taskManagement.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`taskManagement.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </section>
    </div>
  );
};

export default AIInvestigation;
